import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot } from '@angular/router';
import { AuthService } from '@shared/services/auth/auth.service';
import { ROUTES } from '@shared/constants/routes/routes.constants';
import { catchError, delay, map, Observable, of, switchMap } from 'rxjs';
import { ApplicationFacade } from '@app/store/facade/application.facade';
import { Role } from '@shared/enums/Role';

/** Service that guards routes based on User roles. */
@Injectable({
  providedIn: 'root',
})
export class AuthGuardService {
  private authService: AuthService = inject(AuthService);
  private router: Router = inject(Router);
  private applicationFacade: ApplicationFacade = inject(ApplicationFacade);

  /**
   * Route guard that determines whether a route can be activated based on
   * the User's assigned roles and the application's online/offline state.
   *
   * ### Behavior
   * - ** Online **
   *   - Backend health is checked on every guarded navigation, even when roles are cached;
   *   - An unhealthy backend redirects to the offline view;
   *   - User roles are fetched once and reused from the AuthService cache;
   *   - Access is granted only if at least one role is present;
   *   - Missing roles or a failed roles request redirect to the unauthorised view.
   *
   * - ** Offline (with retry) **
   *   - If `isOnline$` initially emits `false`, waits briefly (1 second) and retries once
   *     to handle race conditions during app initialization;
   *   - After retry, if still offline: all routes are allowed.
   * @param route - The route being activated.
   * @param state - The current router state.
   * @returns An Observable emitting `true` if the route can be activated,
   *          or `false` if navigation should be blocked.
   */
  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    return this.applicationFacade.isOnline$.pipe(
      switchMap((isOnline: boolean) => {
        if (!isOnline) {
          return this.applicationFacade.isOnline$.pipe(
            // Wait 1000ms for network/token checks to complete
            delay(1000),
            switchMap((isOnlineAfterRetry: boolean) => {
              // After retry, if still offline allow navigation without role validation
              if (!isOnlineAfterRetry) return of(true);

              // Online after retry, or forced-online route: perform role check
              return this.performRoleCheck();
            })
          );
        }

        // Initially online: perform role-based authorization immediately
        return this.performRoleCheck();
      })
    );
  }

  /**
   * Checks backend health before performing role-based authorization.
   * @private
   */
  private performRoleCheck(): Observable<boolean> {
    return this.authService.checkHealth().pipe(
      switchMap((isHealthy: boolean) => {
        if (!isHealthy) {
          this.router.navigate([`/${ROUTES.OFFLINE}`]).then();
          return of(false);
        }

        return this.authService.getRoles().pipe(
          catchError(() => of<Role[]>([])),
          map((roles: Role[]) => {
            if (roles?.length) return true;

            this.router.navigate([`/${ROUTES.UNAUTHORISED}`]).then();
            return false;
          })
        );
      })
    );
  }
}

/**
 * Factory function for `AuthGuardService` used in route configuration.
 * @param next - The route that is being activated.
 * @param state - The current router state.
 * @returns An observable emitting `true` if the route can be activated, `false` otherwise.
 */
export const AuthGuard: CanActivateFn = (next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> => {
  return inject(AuthGuardService).canActivate(next, state);
};
